import { withAsyncDeadline } from './async-deadline';
// This module rides in several bundles. The wire queue, like the store, belongs to the PAGE.
interface ObjectRead { promise?: Promise<unknown>; attempts: number; retryAt: number; error?: unknown; retried?: boolean }
interface ObjectQueue { active: number; waiting: Array<() => void>; reads?: Map<string, ObjectRead> }
/** An adoption join may retry its failed read once; later joins retain the cooldown. */
export function retryObjectRequest(error: unknown): void {
    const queue = (globalThis as typeof globalThis & { __ftObjectRequestQueue?: ObjectQueue }).__ftObjectRequestQueue;
    for (const read of queue?.reads?.values() ?? []) {
        if (!read.promise && read.error === error && read.attempts === 1 && !read.retried) {
            read.retried = true;
            read.retryAt = 0;
            return;
        }
    }
}
/** Keyed reads share failure history; fresh reads bypass joins/cooldown and replace that history. No retry timers. */
export function objectRequest<T>(send: (signal: AbortSignal) => Promise<T>, key?: string, fresh = false): Promise<T> {
    const host = globalThis as typeof globalThis & { __ftObjectRequestQueue?: ObjectQueue;
        __ftDefinitionStore?: { epoch?: number | null; cacheGeneration?: number } };
    const queue = host.__ftObjectRequestQueue ??= { active: 0, waiting: [] };
    const reads = queue.reads ??= new Map<string, ObjectRead>();
    const identity = key && JSON.stringify([key, host.__ftDefinitionStore?.epoch, host.__ftDefinitionStore?.cacheGeneration]);
    const previous = identity ? reads.get(identity) : undefined;
    if (!fresh && previous?.promise) return previous.promise as Promise<T>;
    if (!fresh && previous && Date.now() < previous.retryAt) return Promise.reject(previous.error);
    const promise = new Promise<T>((resolve, reject) => {
        const done = (): void => { queue.active--; queue.waiting.shift()?.(); };
        const start = (): void => {
            queue.active++;
            // Include body decoding; a hung read must release its page-wide slot.
            void withAsyncDeadline(send, 20000).then(value => { done(); resolve(value); }, error => { setTimeout(() => { done(); reject(error); }, 0); });
        };
        if (queue.active < 2) start(); else queue.waiting.push(start);
    });
    if (!identity) return promise;
    const read: ObjectRead = { attempts: fresh ? 0 : previous?.attempts ?? 0, retryAt: 0, retried: fresh ? undefined : previous?.retried };
    reads.set(identity, read);
    read.promise = promise.then(value => {
        const current = reads.get(identity);
        // Any successful answer ends this identity's failure history, including a late pre-fresh answer.
        // Keep a newer pending promise joinable until its own answer settles.
        if (!current?.promise || current === read) reads.delete(identity);
        else { current.attempts = 0; current.retryAt = 0; current.error = undefined; current.retried = undefined; }
        return value;
    }, error => {
        read.promise = undefined;
        read.error = error;
        read.attempts++;
        read.retryAt = Date.now() + Math.min(60000, 5000 * 2 ** Math.min(read.attempts - 1, 4));
        // Retain only bounded failure history; pending work keeps its shared promise.
        for (const [id, entry] of reads) if (reads.size > 128 && !entry.promise && id !== identity) reads.delete(id);
        throw error;
    });
    return read.promise as Promise<T>;
}

